import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-[#0A091E] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A091E]/90 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#D4AF37] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#0A091E]" />
            </div>
            <span className="font-bold text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
              SRM <span className="text-[#D4AF37]">Cadence</span>
            </span>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Terms of <span className="text-[#D4AF37]">Service</span>
        </h1>
        <p className="text-white/50 mb-8">Last Updated: February 21, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">1. Acceptance of Terms</h2>
            <p className="text-white/70 leading-relaxed">
              By accessing or using the SRM Cadence Ecosystem ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
              If you do not agree to these Terms, you may not access or use the Service. These Terms apply to all users, 
              including employees, managers, administrators, and clients.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">2. Description of Service</h2>
            <p className="text-white/70 leading-relaxed">
              SRM Cadence is an enterprise workforce management platform that provides time tracking, payroll calculations, 
              HR management, secure client communications, and related services. The Service includes web-based applications, 
              APIs, and communication features including SMS, voice calls, and email notifications.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl border-l-4 border-l-[#D4AF37]">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">3. SMS and Voice Communication Consent</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              <strong className="text-white">By registering for SRM Cadence and providing your phone number, you expressly consent to receive:</strong>
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li>Multi-Factor Authentication (MFA) verification codes via SMS and/or automated voice calls</li>
              <li>Shift reminders and "Wake Up" alerts before your scheduled work time</li>
              <li>Time-sensitive notifications about schedule changes, approvals, and system alerts</li>
              <li>Performance accolades and recognition notifications</li>
              <li>Emergency and critical system notifications</li>
            </ul>
            <p className="text-white/70 leading-relaxed mt-4">
              <strong className="text-white">Message Frequency:</strong> Message frequency varies based on your activity and notification preferences. 
              You may receive up to 10 messages per day during active work periods.
            </p>
            <p className="text-white/70 leading-relaxed mt-4">
              <strong className="text-white">Message and Data Rates:</strong> Standard message and data rates may apply depending on your mobile carrier plan.
            </p>
            <p className="text-white/70 leading-relaxed mt-4">
              <strong className="text-white">Opt-Out:</strong> You may opt out of non-essential SMS notifications at any time by texting STOP to our messaging number 
              or by updating your notification preferences in your account settings. Note: Opting out of MFA messages may prevent you from 
              accessing your account. Essential security messages cannot be disabled while your account is active.
            </p>
            <p className="text-white/70 leading-relaxed mt-4">
              <strong className="text-white">Help:</strong> For assistance, text HELP to our messaging number or contact support@srmcadence.com.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">4. User Accounts and Security</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. 
              You agree to:
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Set up and maintain Multi-Factor Authentication (MFA) as required</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Not share your login credentials with any third party</li>
            </ul>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">5. Acceptable Use</h2>
            <p className="text-white/70 leading-relaxed mb-4">You agree not to:</p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Interfere with or disrupt the Service or servers connected to the Service</li>
              <li>Falsify time records, attendance data, or any other information in the system</li>
              <li>Share confidential client information outside authorized channels</li>
              <li>Use the Client Bridge for any purpose other than authorized work activities</li>
            </ul>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">6. Data and Privacy</h2>
            <p className="text-white/70 leading-relaxed">
              Your use of the Service is also governed by our Privacy Policy, which is incorporated into these Terms by reference. 
              All data transmitted through the Service is encrypted using AES-256 encryption. We maintain comprehensive audit trails 
              for compliance with ISO and GDPR requirements.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">7. Intellectual Property</h2>
            <p className="text-white/70 leading-relaxed">
              The Service and its original content, features, and functionality are owned by SRM Cadence and are protected by 
              international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">8. Limitation of Liability</h2>
            <p className="text-white/70 leading-relaxed">
              To the maximum extent permitted by law, SRM Cadence shall not be liable for any indirect, incidental, special, 
              consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other 
              intangible losses, resulting from your access to or use of or inability to access or use the Service.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">9. Termination</h2>
            <p className="text-white/70 leading-relaxed">
              We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, 
              for any reason, including without limitation if you breach the Terms. Upon termination, your right to use the 
              Service will immediately cease.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">10. Changes to Terms</h2>
            <p className="text-white/70 leading-relaxed">
              We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide 
              at least 30 days notice prior to any new terms taking effect. What constitutes a material change will be 
              determined at our sole discretion.
            </p>
          </section>

          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold text-[#D4AF37] mb-4">11. Contact Us</h2>
            <p className="text-white/70 leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-white mt-2">
              Email: legal@srmcadence.com<br />
              Support: support@srmcadence.com
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex justify-center gap-6">
          <Link to="/privacy" className="text-[#D4AF37] hover:text-[#D4AF37]/80">
            Privacy Policy
          </Link>
          <Link to="/login" className="text-white/50 hover:text-white">
            Back to Login
          </Link>
        </div>
      </main>
    </div>
  );
}
